import { Box, Button, FormControl, FormHelperText, Typography, useTheme, styled } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { NoteColor } from 'src/enums'
import { MUIThemeColors } from 'src/mui-theme'

interface ColorPickerProps {
  name: string
  label?: string
  setValue: (name: string, value: string | null | undefined) => void
  value?: string | null
  error?: string
}

interface ColorBoxProps {
  isSelected: boolean
}

export const NOTE_COLOR_HEX_MAP: Record<NoteColor, string> = {
  [NoteColor.DarkRed]: '#aa8684',
  [NoteColor.DarkOrange]: '#9e796c',
  [NoteColor.DarkYellow]: '#bda969',
  [NoteColor.DarkGreen]: '#5da06b',
  [NoteColor.DarkTeal]: '#619398',
  [NoteColor.DarkBlue]: '#5d88c4',
  [NoteColor.DarkNavy]: '#4f649d',
  [NoteColor.DarkPurple]: '#755aa1',
  [NoteColor.LightRed]: '#eb9a96',
  [NoteColor.LightOrange]: '#fad2c5',
  [NoteColor.LightYellow]: '#fdf4c1',
  [NoteColor.LightGreen]: '#c1e0c6',
  [NoteColor.LightTeal]: '#bfd9dc',
  [NoteColor.LightBlue]: '#c5ddf5',
  [NoteColor.LightNavy]: '#bfd3f1',
  [NoteColor.LightPurple]: '#d5c5f9',
}

const DARK_COLORS: NoteColor[] = [
  NoteColor.DarkRed,
  NoteColor.DarkOrange,
  NoteColor.DarkYellow,
  NoteColor.DarkGreen,
  NoteColor.DarkTeal,
  NoteColor.DarkBlue,
  NoteColor.DarkNavy,
  NoteColor.DarkPurple,
]

const LIGHT_COLORS: NoteColor[] = [
  NoteColor.LightRed,
  NoteColor.LightOrange,
  NoteColor.LightYellow,
  NoteColor.LightGreen,
  NoteColor.LightTeal,
  NoteColor.LightBlue,
  NoteColor.LightNavy,
  NoteColor.LightPurple,
]

export const getNoteColorHex = (noteColor?: string | null): string | undefined => {
  if (!noteColor) {
    return undefined
  }

  if ((Object.values(NoteColor) as string[]).includes(noteColor)) {
    return NOTE_COLOR_HEX_MAP[noteColor as NoteColor]
  }

  return noteColor
}

export const getNoteColorKey = (noteColor?: string | null): NoteColor | undefined => {
  if (!noteColor) {
    return undefined
  }

  if ((Object.values(NoteColor) as string[]).includes(noteColor)) {
    return noteColor as NoteColor
  }

  const normalizedHex = noteColor.toLowerCase()
  const entry = Object.entries(NOTE_COLOR_HEX_MAP).find(([, hex]) => hex.toLowerCase() === normalizedHex)

  return entry?.[0] as NoteColor | undefined
}

const ColorBox = styled(Box)<ColorBoxProps>(({ theme, isSelected }) => ({
  width: 25,
  height: 25,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(isSelected && {
    boxShadow: theme.shadows[4],
  }),
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[2],
  },
}))

export const ColorPicker: React.FC<ColorPickerProps> = ({ name, label, setValue, value, error }) => {
  const theme = useTheme()
  const selectedColorKey = getNoteColorKey(value)

  return (
    <FormControl fullWidth error={!!error}>
      <Box
        component="fieldset"
        sx={{
          m: 0,
          p: 0.5,
          border: `1px solid ${theme.palette.inputBorder.main}`,
          borderRadius: 1,
          bgcolor: MUIThemeColors.white,
        }}
      >
        {label && (
          <Box
            component="legend"
            sx={{
              px: 0.75,
              typography: 'caption',
              color: error ? theme.palette.error.main : theme.palette.black.main,
              fontWeight: '500',
            }}
          >
            {label}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            onClick={() => setValue(name, null)}
            disabled={!selectedColorKey}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.3,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              color: selectedColorKey ? theme.palette.primary.main : theme.palette.text.secondary,
              bgcolor: selectedColorKey ? theme.palette.action.selected : 'transparent',
              minWidth: 0,
              textTransform: 'none',
            }}
            title="Clear color"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">Clear</Typography>
          </Button>
        </Box>

        {/* Dark Colors Row */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {DARK_COLORS.map(colorKey => (
            <ColorBox
              key={colorKey}
              onClick={() => setValue(name, colorKey)}
              sx={{ backgroundColor: NOTE_COLOR_HEX_MAP[colorKey] }}
              isSelected={selectedColorKey === colorKey}
              title={colorKey}
            >
              {selectedColorKey === colorKey && <CheckIcon sx={{ color: 'white', fontSize: 20, fontWeight: 'bold' }} />}
            </ColorBox>
          ))}
        </Box>

        {/* Light Colors Row */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {LIGHT_COLORS.map(colorKey => (
            <ColorBox
              key={colorKey}
              onClick={() => setValue(name, colorKey)}
              sx={{ backgroundColor: NOTE_COLOR_HEX_MAP[colorKey] }}
              isSelected={selectedColorKey === colorKey}
              title={colorKey}
            >
              {selectedColorKey === colorKey && <CheckIcon sx={{ color: '#333', fontSize: 20, fontWeight: 'bold' }} />}
            </ColorBox>
          ))}
        </Box>
      </Box>
      {error && <FormHelperText sx={{ mt: 1 }}>{error}</FormHelperText>}
    </FormControl>
  )
}
